import { mockableLinks } from '../constants/config';

const STORAGE_PREFIX = 'ec_api_cache:';
const LEGACY_SUFFIXES = ['_local_data', '_local_data_timestamp'];

const getUserKey = () => localStorage.getItem('ec_current_usid') || '';

const stableStringify = (value) => {
  if (value === undefined || value === null) {
    return '';
  }
  if (typeof value !== 'object') {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }
  const keys = Object.keys(value).sort();
  return `{${keys.map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(',')}}`;
};

const hashString = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash).toString(36);
};

export const normalizePath = (url) => {
  if (!url) {
    return '';
  }
  let path = url.replace(/^https?:\/\/[^/]+\//, '');
  path = path.replace(/^\/+/, '');
  path = path.split('?')[0];
  return path;
};

const findMockableConfig = (path) => mockableLinks.find((entry) => entry.url === path);

export const isCacheable = (config) => {
  const path = normalizePath(config.url);
  const mockable = findMockableConfig(path);
  if (!mockable) {
    return false;
  }
  const allowedMethods = mockable.methods || ['get', 'post'];
  const method = (config.method || 'get').toLowerCase();
  return allowedMethods.includes(method);
};

export const buildCacheKey = (config) => {
  const path = normalizePath(config.url);
  const method = (config.method || 'get').toLowerCase();
  const params = stableStringify(config.params);
  const data = stableStringify(config.data);
  const userKey = getUserKey();
  return `${path}|${method}|${params}|${data}|${userKey}`;
};

const storageKeyFor = (cacheKey) => `${STORAGE_PREFIX}${hashString(cacheKey)}`;

const readEntry = (storageKey) => {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const getTtlSeconds = (config) => {
  const path = normalizePath(config.url);
  const mockable = findMockableConfig(path);
  return mockable ? mockable.ttl_in_seconds : 0;
};

export const get = (config, options = {}) => {
  const { allowStale = false } = options;
  if (!isCacheable(config)) {
    return null;
  }

  const cacheKey = buildCacheKey(config);
  const entry = readEntry(storageKeyFor(cacheKey));
  if (!entry) {
    return null;
  }

  if (entry.userKey !== getUserKey()) {
    return null;
  }

  const isExpired = Date.now() > entry.expiresAt;
  if (isExpired && !allowStale) {
    return null;
  }

  return entry.data;
};

export const set = (config, data, ttlSeconds) => {
  if (!isCacheable(config) || data === undefined) {
    return;
  }

  const cacheKey = buildCacheKey(config);
  const path = normalizePath(config.url);
  const ttl = ttlSeconds || getTtlSeconds(config);
  const entry = {
    data,
    expiresAt: Date.now() + ttl * 1000,
    userKey: getUserKey(),
    path,
    cacheKey
  };

  try {
    localStorage.setItem(storageKeyFor(cacheKey), JSON.stringify(entry));
  } catch (error) {
    console.warn('apiCache.set failed', error);
  }
};

const removeLegacyKeys = (path) => {
  localStorage.removeItem(`${path}${LEGACY_SUFFIXES[0]}`);
  localStorage.removeItem(`${path}${LEGACY_SUFFIXES[1]}`);
};

export const invalidate = (path) => {
  const normalizedPath = normalizePath(path);
  const keysToRemove = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key || !key.startsWith(STORAGE_PREFIX)) {
      continue;
    }
    const entry = readEntry(key);
    if (entry && entry.path === normalizedPath) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => localStorage.removeItem(key));
  removeLegacyKeys(normalizedPath);
};

export const clearAll = (options = {}) => {
  const except = new Set(options.except || []);
  const keysToRemove = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) {
      continue;
    }
    if (key.startsWith(STORAGE_PREFIX)) {
      const entry = readEntry(key);
      if (!entry || !except.has(entry.path)) {
        keysToRemove.push(key);
      }
      continue;
    }
    if (key.endsWith('_local_data_timestamp')) {
      const path = key.slice(0, -'_local_data_timestamp'.length);
      if (!except.has(path)) {
        keysToRemove.push(key);
      }
      continue;
    }
    if (key.endsWith('_local_data')) {
      const path = key.slice(0, -'_local_data'.length);
      if (!except.has(path)) {
        keysToRemove.push(key);
      }
    }
  }

  keysToRemove.forEach((key) => localStorage.removeItem(key));
};

export const createCachedResponse = (config, data) => ({
  data,
  status: 200,
  statusText: 'OK',
  headers: {},
  config
});

export const getTtl = getTtlSeconds;
